import _debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Form } from "semantic-ui-react";

import "lib/Form/components/FormColorPicker/FormColorPicker.scss";

import type { ChangeEvent } from "react";
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import type { FormFieldProps } from "semantic-ui-react";

type TypeFormColorPicker<T extends FieldValues> = {
  name: Path<T>;
  formFieldProps?: FormFieldProps;
};

const DEFAULT_COLOR = "#000000";
const COMMIT_DELAY = 150;

const ColorInput = <T extends FieldValues>({ field }: { field: ControllerRenderProps<T, Path<T>> }) => {
  const [color, setColor] = useState<string>(field.value ?? "");

  const commitColor = useMemo(() => _debounce(field.onChange, COMMIT_DELAY), [field.onChange]);

  useEffect(() => setColor(field.value ?? ""), [field.value]);
  useEffect(() => () => commitColor.cancel(), [commitColor]);

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setColor(target.value);
    commitColor(target.value);
  };

  const handleBlur = () => {
    commitColor.flush();
    field.onBlur();
  };

  return (
    <input
      type="color"
      className="form-color-picker"
      name={field.name}
      ref={field.ref}
      value={color || DEFAULT_COLOR}
      disabled={field.disabled}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

export const FormColorPicker = <T extends FieldValues>({ name, formFieldProps }: TypeFormColorPicker<T>) => {
  const { control } = useFormContext<T>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { invalid, isTouched } }) => (
        <Form.Field {...formFieldProps} error={invalid && isTouched}>
          <ColorInput field={field} />
        </Form.Field>
      )}
    />
  );
};
